import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, TrendingUp, Award, Book, Flame, Target, CheckCircle, Clock } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const StudentProgress = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const res = await api.get('/analytics/student-detailed');
      setData(res.data);
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Layout title="My Progress"><div className="text-center py-12 text-slate-500">Loading progress...</div></Layout>;
  if (!data) return <Layout title="My Progress"><div className="text-center py-12 text-slate-500">No progress data</div></Layout>;

  const { stats, subject_mastery, quiz_attempts } = data;

  // Build score history chart data from quiz_attempts
  const scoreHistory = quiz_attempts.slice(0, 20).reverse().map((q, i) => ({
    attempt: i + 1,
    score: Math.round(q.score),
    date: q.created_at?.slice(5, 10) || '',
  }));

  // Mastery chart data
  const masteryData = subject_mastery.map(s => ({
    subject: s.subject_code || s.subject_name?.slice(0, 6),
    score: s.avg_score,
    concepts: s.concepts_mastered,
  }));

  const subjectColors = { PHY: '#3B82F6', CHEM: '#10B981', BIO: '#8B5CF6', MATH: '#F59E0B', SCI: '#06B6D4' };

  return (
    <Layout title="My Progress">
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 data-testid="progress-title" className="text-xl sm:text-2xl font-bold text-indigo-900">My Progress</h1>
            <p className="text-slate-600 text-xs sm:text-sm">Track your learning journey</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/student')} className="rounded-full text-xs sm:text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {[
            { label: 'Quizzes Taken', value: stats.total_quizzes, icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-50' },
            { label: 'Avg Score', value: `${stats.avg_score}%`, icon: Award, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Models Viewed', value: stats.models_viewed, icon: Book, color: 'text-cyan-600', bg: 'bg-cyan-50' },
            { label: 'Active Days', value: stats.active_days, icon: Flame, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          ].map(s => (
            <Card key={s.label} className="border-slate-200">
              <CardContent className="p-3 sm:pt-6 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full ${s.bg} flex items-center justify-center flex-shrink-0`}>
                    <s.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${s.color}`} />
                  </div>
                  <div>
                    <p className={`text-lg sm:text-2xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-[10px] sm:text-xs text-slate-600">{s.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Score History Chart */}
        {scoreHistory.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6 pb-2">
              <CardTitle className="text-indigo-900 text-sm sm:text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Score History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 sm:p-6 pt-0">
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={scoreHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(val) => [`${val}%`, 'Score']} />
                    <Line type="monotone" dataKey="score" stroke="#4F46E5" strokeWidth={2} dot={{ r: 4, fill: '#4F46E5' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subject Mastery */}
        {subject_mastery.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6 pb-2">
              <CardTitle className="text-indigo-900 text-sm sm:text-base flex items-center gap-2">
                <Award className="w-4 h-4" /> Subject Mastery
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-2 space-y-4">
              {subject_mastery.map(s => {
                const masteryPct = Math.min(100, s.avg_score);
                const color = subjectColors[s.subject_code] || '#4F46E5';
                return (
                  <div key={s.subject_id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                        <span className="font-medium text-slate-800 text-sm">{s.subject_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{s.concepts_mastered} concepts</Badge>
                        <span className="text-sm font-bold" style={{ color }}>{s.avg_score}%</span>
                      </div>
                    </div>
                    <Progress value={masteryPct} className="h-2" />
                    <div className="flex justify-between text-[10px] text-slate-400">
                      <span>{s.attempts} attempts</span>
                      <span>Best: {s.best_score}%</span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Subject Performance Bar Chart */}
        {masteryData.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6 pb-2">
              <CardTitle className="text-indigo-900 text-sm sm:text-base">Performance by Subject</CardTitle>
            </CardHeader>
            <CardContent className="p-2 sm:p-6 pt-0">
              <div className="h-48 sm:h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={masteryData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="subject" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(val) => [`${val}%`, 'Avg Score']} />
                    <Bar dataKey="score" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Quiz Attempts */}
        <Card className="border-slate-200">
          <CardHeader className="p-4 sm:p-6 pb-2">
            <CardTitle className="text-indigo-900 text-sm sm:text-base flex items-center gap-2">
              <Clock className="w-4 h-4" /> Recent Quiz Attempts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-2 space-y-3">
            {quiz_attempts.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No quiz attempts yet. Start learning!</p>
            ) : quiz_attempts.slice(0, 10).map((q, i) => (
              <div key={i} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${q.score >= 70 ? 'bg-emerald-500' : q.score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}>
                    {Math.round(q.score)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 text-xs sm:text-sm">Quiz {q.quiz_id?.slice(0, 8)}</p>
                    <p className="text-[10px] sm:text-xs text-slate-500">{q.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {q.first_attempt && <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border-0">1st</Badge>}
                  {q.score >= 70 && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
